import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router';
import { ApiError } from './api/client.js';
import { AuthProvider } from './auth/AuthProvider.js';
import { I18nProvider } from './i18n/I18nProvider.js';
import { PwaHost } from './components/PwaHost.js';
import { routes } from './routes.js';
import { theme } from './theme.js';

/**
 * The application, assembled.
 *
 * Provider order is not incidental: the router is innermost because routing
 * decisions read the auth context, and the auth context issues requests that
 * the query client caches.
 *
 * `I18nProvider` is outermost of the three because everything inside it renders
 * text -- including the auth boundary, which is the first thing a signed-out
 * visitor sees and would otherwise be the one screen stuck in one language.
 */

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Bookkeeping figures do not change behind the user's back the way a
        // feed does -- they change because this person changed them, and a
        // mutation invalidates what it affects. Refetching on every window
        // focus would put steady load on the API for data that is already
        // right.
        refetchOnWindowFocus: false,
        staleTime: 30_000,
        retry: (failureCount, error) => {
          // A refusal is an answer, not a fault: retrying a 403 or a 422 asks
          // the same question and gets the same refusal, three times slower.
          // Only 5xx and unreachability are worth another attempt.
          if (error instanceof ApiError && error.status < 500) {
            return false;
          }
          return failureCount < 2;
        },
      },
      mutations: {
        // Never automatic. A mutation here posts to an immutable ledger, and a
        // retry of a request that actually succeeded would post it twice. The
        // API's idempotency keys are what make a deliberate retry safe.
        retry: false,
      },
    },
  });
}

const router = createBrowserRouter(routes);

// Both created once, at module scope. A QueryClient built during render would
// be a new empty cache on every render, and a router rebuilt the same way would
// lose the history stack.
const queryClient = createQueryClient();

export function App(): ReactNode {
  return (
    <MantineProvider theme={theme} defaultColorScheme="auto">
      <I18nProvider>
        <Notifications position="top-right" />
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            {/*
              Inside AuthProvider because the offline queue has to be told which
              company it is queuing for, and above the router because the update
              prompt and the "your backlog went through" toast belong to the
              application rather than to whichever page happens to be open.
            */}
            <PwaHost />
            <RouterProvider router={router} />
          </AuthProvider>
        </QueryClientProvider>
      </I18nProvider>
    </MantineProvider>
  );
}
